import { AfterViewChecked, Component, Input, OnChanges, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { NgClass, NgStyle } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subject, Subscription, combineLatest, from, interval, lastValueFrom, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  Address,
  ApplicationTypes,
  AzureStorageDocument,
  UploadedDocumentRef,
  Individual,
  IndividualDetailsListValue,
  IndividualFormValue,
  ParsedAddress,
  PropertyOwnerWithAddressValue,
  SaveApplicationPartiesData,
  SaveApplicationPartyIndividualInput,
  SignerRoleTypes,
  UpdateIndividualData,
  countryOfCitizenshipByName,
  roleNeedsCitizenshipResidencyAlert,
  filesToBase64Files,
  parseAddressComponentsToString,
  parseRawAddress,
  IndividualWithResult,
  ApplicationAddressApi,
  individualRoleLabels,
  individualSignerRole,
} from '@portal-workspace/grow-shared-library';
import {
  PortalHotToastService,
  collectIndividualIdDocumentUploads,
  individualIdDocumentRefs,
  apiIndividualDob,
  apiIndividualResidentialAddress,
  apiIndividualTitle,
  setupUntilDestroy,
  tagIndividualIdDocumentFiles,
} from '@portal-workspace/grow-ui-library';
import { MARK, Mark } from '@portal-workspace/grow-ui-library/mark';
import { AbstractControlValueAccessor } from '../abstract-control-value-accessor';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';
import { MarkDirective } from '../../directives/mark-as-dirty.directive';
import { MessageBoxComponent } from '../message-box/message-box.component';
import { TagBoxComponent } from '../message-box/tag-box.component';
import { AddressSplitPipe } from '../../pipes/address-split.pipe';
import { SanitizeHtmlPipe } from '../../pipes/sanitize-html.pipe';
import { SignerRolePipe } from '../../pipes/signer-role.pipe';
import { DigitalIdService } from '../../../../../../apps/portal2/src/app/service/digital-id.service';
import { ApplicationService } from '../../../../../../apps/portal2/src/app/service/application.service';

// How often unsaved (and valid) edits are flushed to the backend in the background.
const AUTOSAVE_INTERVAL_MS = 30_000;

type RowInfo = {
  formControl: FormControl<IndividualFormValue>,
  individual: IndividualWithResult,
  // Cached rather than read off the control in the template: see refreshRowValidity.
  invalid: boolean,
  // An ID document is already held for this individual, so the form's required upload is
  // satisfied without re-attaching the file (which is never read back into the form).
  idDocumentProvided: boolean,
  idDocuments: UploadedDocumentRef[],
  deletedIdDocuments: UploadedDocumentRef[],
};

// The address as it will come back from the server once saved, so change detection on the next edit
// diffs against the same shape it would have read. Not what gets sent - the save flattens the parts
// into UpdateIndividualData itself.
const baselineAddress = (unformatted: string, parsed: ParsedAddress | null): ApplicationAddressApi => ({
  unformattedAddress: unformatted,
  unitNumber: parsed?.unit_number ?? '',
  streetNumber: parsed?.street_number ?? '',
  streetName: parsed?.street_name ?? '',
  streetType: parsed?.street_type ?? '',
  suburb: parsed?.locality ?? '',
  state: parsed?.region ?? '',
  postcode: parsed?.postal_code ?? '',
  isManualInput: false,
});

// Pre-submission "Individual Details" review step: lists every individual saved against the
// application (ApplicationIndividual), one expandable <individual-form> per row (only one open at
// a time), plus a primary-contact selector. Edits are persisted through /save-application-parties:
// on row switch, on a 30s background timer, and via flushChanges() which the wizard pages await
// before advancing. Registers as a form control so the wizard's step FormGroup stays invalid until
// every individual's required fields are complete and a primary contact is chosen.
@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'individual-details-list',
  templateUrl: './individual-details-list.component.html',
  styleUrls: ['./individual-details-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'block' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IndividualDetailsListComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => IndividualDetailsListComponent), multi: true },
    { provide: MARK, useExisting: forwardRef(() => IndividualDetailsListComponent) },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    NgClass,
    NgStyle,
    AddressSplitPipe,
    SanitizeHtmlPipe,
    SignerRolePipe,
    IndividualFormComponent,
    MarkDirective,
    MessageBoxComponent,
    TagBoxComponent,
  ],
})
export class IndividualDetailsListComponent
  extends AbstractControlValueAccessor<IndividualDetailsListValue>
  implements OnInit, OnChanges, AfterViewChecked, Mark, Validator {

  @Input({ required: true }) applicationId!: number;
  @Input({ required: true }) applicationType!: ApplicationTypes;
  // Base metadata attached to uploaded ID documents (pages pass their step9OtherDocMetadata:
  // legalname/abn or consumer name/dob) so the docs group correctly under the Documents tab.
  @Input({ required: false }) idDocumentMetadata: { [key: string]: string } = {};
  // The quoting step's application-level "Property owner" answer. When it is Yes the pricing was
  // based on a property owner, so at least one individual has to back that up here.
  @Input({ required: false }) propertyOwnerRequired = false;

  subscriptions: Subscription[] = [];
  markObservable: Subject<boolean> = new Subject<boolean>();

  columnsToDisplay = ['dir', 'name', 'dob', 'address', 'businessRole', 'applicationRole', 'status'];
  rows: RowInfo[] = [];
  tableData: IndividualWithResult[] = [];
  expandedElement: IndividualWithResult | null = null;
  loaded = false;
  autosaveFailed = false;
  documentsLoadFailed = false;
  saveInProgress = false;

  private rowValiditySyncQueued = false;
  private saveCompletion: Promise<boolean> = Promise.resolve(true);

  formControlPrimaryContact: FormControl<string | null>;

  private onValidatorChangeFn?: () => void;

  constructor(private formBuilder: FormBuilder,
              private digitalIdService: DigitalIdService,
              private applicationService: ApplicationService,
              private portalHotToastService: PortalHotToastService) {
    super();
    this.formControlPrimaryContact = this.formBuilder.control(null, [Validators.required]);
  }

  ngAfterViewChecked(): void {
    this.syncRowValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyOwnerRequired'] && !changes['propertyOwnerRequired'].firstChange) {
      this.onValidatorChangeFn?.();
    }
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.reload();
    this.subscriptions.push(interval(AUTOSAVE_INTERVAL_MS).pipe(
      tap(() => this.autosave()),
    ).subscribe());
  }

  // Reloads the individuals from the backend (source of truth: ApplicationIndividual rows).
  // Called on init and by the wizard page every time this step is (re-)entered, since earlier
  // steps (KYB key people / guarantors) may have added or removed individuals in between.
  // `after` lets the page hand over its in-flight draft save - the list is marked stale (which
  // gates the step's Next button) straight away, but only re-read once that write has landed.
  reload(after?: Promise<unknown>) {
    if (!this.applicationId) {
      return;
    }
    this.loaded = false;
    this.documentsLoadFailed = false;
    // on a microtask - flipping validity synchronously inside the stepper's selection event would
    // trip ExpressionChangedAfterItHasBeenChecked
    Promise.resolve().then(() => this.onValidatorChangeFn?.());
    if (after) {
      after.then(() => this.doReload());
    } else {
      this.doReload();
    }
  }

  private doReload() {
    // The documents are read alongside the individuals to tell which ones already have an ID
    // document on file. Keep a failed lookup distinct from a confirmed missing document.
    const documents$ = this.applicationService.listApplicationDocumentFn(this.applicationId).pipe(
      catchError(() => {
        this.documentsLoadFailed = true;
        return of(null as AzureStorageDocument[] | null);
      }),
    );
    this.subscriptions.push(combineLatest([
      this.digitalIdService.getApplicationIndividualsFn(this.applicationId),
      documents$,
    ]).pipe(
      this.portalHotToastService.topMenuLoadingObservable(),
      tap(([payload, documents]) => {
        const individuals = (payload.individuals ?? []).filter(individual => individual.isDeleted !== true);
        this.rows = individuals.map(individual => {
          const idDocuments = individualIdDocumentRefs(
            individual.idDocuments,
            documents ?? [],
            // guid, not id: the document rows are keyed by the guid, and individualIdDocumentRefs
            // takes (string|number)[], so the numeric id would compile and list nothing.
            [individual.guid],
            `${individual.givenName ?? ''} ${individual.surname ?? ''}`,
          );
          return {
            formControl: this.formBuilder.control(this.toIndividualFormInitialValue(individual)),
            individual,
            invalid: false,
            // Do not ask for another upload when the listing failed and nothing is recorded against
            // the individual either; validation below blocks navigation until the lookup succeeds.
            idDocumentProvided: idDocuments.length > 0 || !documents,
            idDocuments,
            deletedIdDocuments: [],
          };
        });
        this.tableData = individuals;
        this.expandedElement = null;
        const primary = individuals.find(individual => individual.isPrimaryContact);
        this.formControlPrimaryContact.setValue(primary?.guid ?? null);
        this.loaded = true;
        this.propagateValue();
        // revalidate whenever any nested individual-form value changes so the wizard's Next
        // button gating reflects live validity
        for (const row of this.rows) {
          this.subscriptions.push(row.formControl.valueChanges.pipe(
            tap(() => this.onValidatorChangeFn?.()),
          ).subscribe());
        }
      }),
    ).subscribe());
  }

  private toIndividualFormInitialValue(individual: IndividualWithResult): IndividualFormValue {
    return {
      title: apiIndividualTitle(individual),
      firstName: individual.givenName ?? '',
      middleName: individual.middleName ?? '',
      lastName: individual.surname ?? '',
      dob: apiIndividualDob(individual)!,
      hasAlias: !!individual.hasAlias,
      aliasNames: individual.aliasNames ?? null,
      countryOfCitizenship: countryOfCitizenshipByName(individual.countryOfCitizenship ?? ''),
      email: individual.email ?? '',
      mobileNumber: individual.mobileNumber ?? '',
      // apiIndividualResidentialAddress, not component-utils' homeAddressFormValue: this form is
      // edited and saved back, so it keeps the parse of the unformatted address that fills the street
      // parts when only the unformatted string was stored.
      residentialAddress: apiIndividualResidentialAddress(individual),
      propertyOwner: this.toPropertyOwnerFormValue(individual),
      identification: (individual.identification?.type && individual.identification?.number)
        ? { idType: individual.identification.type, idNumber: individual.identification.number }
        : null,
      idDocument: null,
    };
  }

  private toPropertyOwnerFormValue(individual: IndividualWithResult): PropertyOwnerWithAddressValue {
    if (!individual.property?.isOwner) {
      return null;
    }
    const address = individual.property.address;
    return {
      propertyOwner: true,
      propertyValue: individual.property.value ?? undefined,
      mortgageValue: individual.property.mortgageBalance ?? undefined,
      address: address ? {
        address: address.unformattedAddress ?? '',
        StreetNumber: address.streetNumber ?? '',
        StreetName: address.streetName ?? '',
        StreetType: address.streetType ?? '',
        Suburb: address.suburb ?? '',
        State: address.state ?? '',
        UnitNumber: address.unitNumber ?? '',
        Postcode: address.postcode ?? '',
      } : undefined,
    };
  }

  rowInfo(individual: IndividualWithResult): RowInfo | undefined {
    return this.rows.find(row => row.individual.id === individual.id);
  }

  rowFormControl(individual: IndividualWithResult): FormControl<IndividualFormValue> | undefined {
    return this.rowInfo(individual)?.formControl;
  }

  rowInvalid(individual: IndividualWithResult): boolean {
    return this.rowInfo(individual)?.invalid ?? false;
  }

  // The table shows the persisted record, not the open row's in-flight edits, so it stays in step
  // with displayDob and with what the legacy bindings these replaced used to read. Saved rows are
  // updated in place by applySavedValuesToBaseline, so edits appear once they land.

  displayName(individual: IndividualWithResult): string {
    return `${individual.givenName ?? ''} ${individual.surname ?? ''}`.trim();
  }

  displayDob(individual: IndividualWithResult): string {
    const dob = apiIndividualDob(individual);
    return dob?.isValid() ? dob.format('DD/MM/YYYY') : '—';
  }

  displayAddress(individual: IndividualWithResult): string {
    const address = apiIndividualResidentialAddress(individual);
    if (!address) {
      return '';
    }
    // Same fallback as the address pipe this replaced: a record captured field by field carries no
    // unformatted line.
    return (address.address ?? '').trim() || parseAddressComponentsToString(
      address.UnitNumber, address.StreetNumber, address.StreetName, address.StreetType,
      address.Suburb, address.State, address.Postcode,
    );
  }

  // Every business role, not the single Role string this replaced: an individual holding several
  // (a director who is also a shareholder) used to read as only the highest-precedence one.
  displayBusinessRole(individual: IndividualWithResult): string {
    return individualRoleLabels(individual).join(', ');
  }

  displaySignerRole(individual: IndividualWithResult): SignerRoleTypes {
    return individualSignerRole(individual);
  }

  residencyAlertFor(individual: IndividualWithResult): boolean {
    return roleNeedsCitizenshipResidencyAlert(individualSignerRole(individual));
  }

  rowIdDocumentProvided(individual: IndividualWithResult): boolean {
    return this.rowInfo(individual)?.idDocumentProvided ?? false;
  }

  rowIdDocuments(individual: IndividualWithResult): UploadedDocumentRef[] {
    return this.rowInfo(individual)?.idDocuments ?? [];
  }

  onIdDocumentDelete(individual: IndividualWithResult, document: UploadedDocumentRef): void {
    const row = this.rowInfo(individual);
    if (!row) return;
    row.idDocuments = row.idDocuments.filter(candidate => candidate.name !== document.name);
    if (!row.deletedIdDocuments.some(candidate => candidate.name === document.name)) {
      row.deletedIdDocuments = [...row.deletedIdDocuments, document];
    }
    row.idDocumentProvided = row.idDocuments.length > 0 || !!row.formControl.value?.idDocument?.length;
    this.onValidatorChangeFn?.();
  }

  /**
   * Copies each row's validity onto the row, on a microtask, for the status column to read.
   *
   * Each nested <individual-form> registers its validators as it is created, which turns its row
   * invalid part-way through the very change detection pass that has already checked that row's
   * status cell, so reading control.invalid straight from the template answers differently twice in
   * one pass (NG0100). Settling the value a microtask later - as reload() does for the same reason -
   * lets the next pass read a stable one. Only re-running when something actually differs is what
   * stops the resulting pass from scheduling another.
   */
  private syncRowValidity(): void {
    if (this.rowValiditySyncQueued || this.rows.every(row => row.invalid === row.formControl.invalid)) {
      return;
    }
    this.rowValiditySyncQueued = true;
    Promise.resolve().then(() => {
      this.rowValiditySyncQueued = false;
      for (const row of this.rows) {
        row.invalid = row.formControl.invalid;
      }
    });
  }

  isTableRowExpanded(individual: IndividualWithResult): boolean {
    return this.expandedElement?.guid === individual.guid;
  }

  // Only one individual is open at a time: expanding a row collapses the previous one. The
  // collapsed row's form control (and any unsaved edits) is retained, and any valid pending
  // changes are flushed to the backend in the background.
  expandTableRow(individual: IndividualWithResult) {
    const expandingNewRow = !this.isTableRowExpanded(individual);
    this.expandedElement = this.isTableRowExpanded(individual) ? null : individual;
    if (expandingNewRow || this.expandedElement === null) {
      this.autosave();
    }
  }

  primaryContactName(): string {
    const id = this.formControlPrimaryContact.value;
    const row = this.rows.find(r => r.individual.guid === id);
    return row ? `${row.individual.givenName ?? ''} ${row.individual.surname ?? ''}`.trim() : '';
  }

  // Primary contact changes are persisted immediately through the dedicated endpoint (which
  // enforces exclusivity server-side) rather than riding on the row saves.
  onPrimaryContactSelectionChange(individualId: string) {
    this.subscriptions.push(this.digitalIdService.updatePrimaryContact(this.applicationId, individualId).pipe(
      this.portalHotToastService.spinnerObservable(),
      this.portalHotToastService.snackBarObservable('Primary contact updated'),
      tap(() => {
        for (const row of this.rows) {
          row.individual.isPrimaryContact = row.individual.guid === individualId;
        }
        this.propagateValue();
      }),
    ).subscribe());
  }

  // ============ change tracking (value diffs, mirrors the KYC tab approach: several custom
  // controls mark themselves dirty on load, so the Angular dirty flag cannot be trusted) ============

  private rowChanged(row: RowInfo): boolean {
    const initial = row.individual;
    const v = row.formControl.value;
    const differs = (original: unknown, current: unknown) => (original ?? '') !== (current ?? '');
    const identification = initial.identification;
    return differs(initial.title, v?.title?.type)
      || differs(initial.givenName, v?.firstName)
      || differs(initial.middleName, v?.middleName)
      || differs(initial.surname, v?.lastName)
      || differs(initial.dateOfBirth, v?.dob ? v.dob.format('YYYY-MM-DD') : '')
      || differs(initial.homeAddress?.unformattedAddress, v?.residentialAddress?.address)
      || differs(initial.email, v?.email)
      || differs(initial.mobileNumber, v?.mobileNumber)
      || (!!initial.hasAlias !== !!v?.hasAlias)
      || differs(initial.aliasNames, v?.hasAlias ? v?.aliasNames : null)
      || differs(initial.countryOfCitizenship, v?.countryOfCitizenship?.name)
      || differs(
        (identification?.type && identification?.number) ? `${identification.type} ${identification.number}` : '',
        (v?.identification?.idType && v?.identification?.idNumber) ? `${v.identification.idType} ${v.identification.idNumber}` : '')
      || this.propertyOwnerChanged(initial, v?.propertyOwner)
      // pending in-memory ID document files count as a change so the next flush uploads them
      || (v?.idDocument ?? []).length > 0
      || row.deletedIdDocuments.length > 0;
  }

  private propertyOwnerChanged(initial: IndividualWithResult, current: NonNullable<IndividualFormValue>['propertyOwner'] | undefined): boolean {
    const { isOwner, value } = this.extractPropertyOwner(current);
    if (!!initial.property?.isOwner !== isOwner) {
      return true;
    }
    if (!isOwner) {
      return false;
    }
    const differs = (original: unknown, current: unknown) => (original ?? '') !== (current ?? '');
    return differs(initial.property?.value, value?.propertyValue)
      || differs(initial.property?.mortgageBalance, value?.mortgageValue)
      || differs(initial.property?.address?.unformattedAddress, value?.address?.address);
  }

  // formControlPropertyOwner's value is a PropertyOwnerWithAddressValue since individual-form is
  // used here with the default withPropertyOwnerAddress=true; the plain-boolean YesNoValue variant
  // is normalized to the same shape so callers only ever branch on isOwner.
  private extractPropertyOwner(propertyOwner: NonNullable<IndividualFormValue>['propertyOwner'] | undefined): {
    isOwner: boolean,
    value: Exclude<PropertyOwnerWithAddressValue, null> | null,
    parsedAddress: ParsedAddress | null,
  } {
    const value = (propertyOwner && typeof propertyOwner === 'object') ? propertyOwner : null;
    const isOwner = value ? !!value.propertyOwner : !!propertyOwner;
    const parsedAddress = (isOwner && value?.address?.address) ? parseRawAddress(value.address.address) : null;
    return { isOwner, value, parsedAddress };
  }

  private changedRows(): RowInfo[] {
    return this.rows.filter(row => !!row.individual.guid && this.rowChanged(row));
  }

  hasUnsavedChanges(): boolean {
    return this.changedRows().length > 0;
  }

  // ============ persistence ============

  private buildUpdateIndividualData(row: RowInfo): UpdateIndividualData {
    const individual = row.individual;
    const v = row.formControl.value;
    const addressValue = v?.residentialAddress ?? null;
    const parsedAddress = addressValue ? parseRawAddress(addressValue.address) : null;
    // This list edits contact and property details, never the role, so the role travels as an echo
    // of what is stored - which is why it is derived here rather than read off a control.
    const signerRole = individualSignerRole(individual);
    return {
      title: v?.title?.type,
      firstName: v?.firstName,
      lastName: v?.lastName,
      middleName: v?.middleName,
      birthdate: v!.dob.format('YYYY-MM-DD'),
      country: 'Australia',
      isSigner: signerRole === 'Signer' || signerRole === 'GuarantorSigner',
      email: v?.email,
      mobileNumber: v?.mobileNumber,
      rawAddress: addressValue ? addressValue.address : '',
      unitNumber: parsedAddress ? parsedAddress.unit_number ?? '' : '',
      streetNumber: parsedAddress ? parsedAddress.street_number : '',
      streetName: parsedAddress ? parsedAddress.street_name : '',
      streetType: parsedAddress ? parsedAddress.street_type ?? '' : '',
      state: parsedAddress ? parsedAddress.region ? parsedAddress.region : '' : '',
      suburb: parsedAddress ? parsedAddress.locality : '',
      postcode: parsedAddress ? parsedAddress.postal_code : '',
      guarantor: individual.isGuarantor,
      contact: individual.isPrimaryContact ? 'primaryContact' : 'contact',
      hasAlias: !!v?.hasAlias,
      aliasNames: v?.hasAlias ? (v?.aliasNames ?? null) : null,
      countryOfCitizenship: v?.countryOfCitizenship?.name ?? null,
      identificationType: v?.identification?.idType ?? null,
      identificationNumber: v?.identification?.idNumber ?? null,
      ...this.buildUpdatePropertyOwnerData(v?.propertyOwner),
    };
  }

  private buildUpdatePropertyOwnerData(propertyOwner: NonNullable<IndividualFormValue>['propertyOwner'] | undefined): Pick<UpdateIndividualData,
    'propertyOwner' | 'propertyValue' | 'mortgageBalance' | 'propertyOwnedRawAddress' | 'propertyOwnedUnitNumber' |
    'propertyOwnedStreetNumber' | 'propertyOwnedStreetName' | 'propertyOwnedStreetType' | 'propertyOwnedSuburb' |
    'propertyOwnedState' | 'propertyOwnedPostcode'> {
    const { isOwner, value, parsedAddress } = this.extractPropertyOwner(propertyOwner);
    if (!isOwner) {
      return { propertyOwner: false, propertyValue: null, mortgageBalance: null };
    }
    const address = value?.address ?? null;
    return {
      propertyOwner: true,
      propertyValue: value?.propertyValue ?? null,
      mortgageBalance: value?.mortgageValue ?? null,
      propertyOwnedRawAddress: address ? address.address : '',
      propertyOwnedUnitNumber: parsedAddress ? parsedAddress.unit_number ?? '' : '',
      propertyOwnedStreetNumber: parsedAddress ? parsedAddress.street_number : '',
      propertyOwnedStreetName: parsedAddress ? parsedAddress.street_name : '',
      propertyOwnedStreetType: parsedAddress ? parsedAddress.street_type ?? '' : '',
      propertyOwnedSuburb: parsedAddress ? parsedAddress.locality : '',
      propertyOwnedState: parsedAddress?.region ?? '',
      propertyOwnedPostcode: parsedAddress ? parsedAddress.postal_code : '',
    };
  }

  // After a successful save the loaded record is updated in place (instead of a full reload,
  // which would rebuild the forms and collapse the open row mid-edit) so subsequent change
  // detection diffs against the persisted state.
  private applySavedValuesToBaseline(row: RowInfo) {
    const v = row.formControl.value;
    if (!v) {
      return;
    }
    const individual = row.individual;
    individual.title = v.title?.type ?? '';
    individual.givenName = v.firstName;
    individual.middleName = v.middleName;
    individual.surname = v.lastName;
    individual.dateOfBirth = v.dob ? v.dob.format('YYYY-MM-DD') : individual.dateOfBirth;
    individual.email = v.email;
    individual.mobileNumber = v.mobileNumber;
    individual.hasAlias = !!v.hasAlias;
    individual.aliasNames = v.hasAlias ? (v.aliasNames ?? null) : null;
    individual.countryOfCitizenship = v.countryOfCitizenship?.name;
    individual.identification = {
      type: v.identification?.idType ?? null,
      number: v.identification?.idNumber ?? null,
    };
    if (v.residentialAddress?.address) {
      individual.homeAddress = baselineAddress(v.residentialAddress.address, parseRawAddress(v.residentialAddress.address));
    }
    const propertyOwner = this.extractPropertyOwner(v.propertyOwner);
    individual.property = {
      isOwner: propertyOwner.isOwner,
      value: propertyOwner.isOwner ? propertyOwner.value?.propertyValue ?? null : null,
      mortgageBalance: propertyOwner.isOwner ? propertyOwner.value?.mortgageValue ?? null : null,
      address: (propertyOwner.isOwner && propertyOwner.value?.address?.address)
        ? baselineAddress(propertyOwner.value.address.address, propertyOwner.parsedAddress)
        : propertyOwner.isOwner ? individual.property?.address ?? null : null,
    };
  }

  // Background flush: saves changed rows whose forms are valid, silently. Invalid rows keep
  // their in-memory edits and are picked up by a later flush once completed. No success toast -
  // this runs on a timer / row switch and would otherwise spam the user.
  private autosave() {
    void this.queueSave({ silent: true });
  }

  // Explicit flush used by the wizard before navigating away from this step. Resolves true when
  // nothing was pending or everything pending was saved successfully.
  async flushChanges(): Promise<boolean> {
    return await this.queueSave({ silent: false });
  }

  private queueSave(opts: { silent: boolean }): Promise<boolean> {
    const queued = this.saveCompletion.then(() => lastValueFrom(this.doSave(opts)));
    this.saveCompletion = queued;
    return queued;
  }

  private doSave(opts: { silent: boolean }) {
    const changed = this.changedRows().filter(row => row.formControl.valid);
    if (!changed.length) {
      return of(!this.changedRows().length);
    }
    const payload: SaveApplicationPartiesData = {
      applicationId: this.applicationId,
      applicationType: this.applicationType,
      individuals: changed.map((row): SaveApplicationPartyIndividualInput => ({
        individualId: row.individual.guid,
        data: this.buildUpdateIndividualData(row),
      })),
    };
    this.saveInProgress = true;
    let save$ = this.digitalIdService.saveApplicationPartiesFn(payload).pipe(
      tap(() => {
        for (const row of changed) {
          this.applySavedValuesToBaseline(row);
        }
      }),
      switchMap(() => this.uploadPendingIdDocuments(changed)),
    );
    if (!opts.silent) {
      save$ = save$.pipe(
        this.portalHotToastService.spinnerObservable(),
        this.portalHotToastService.snackBarObservable('Individual details saved'),
      );
    }
    return save$.pipe(
      tap(() => {
        this.autosaveFailed = false;
        this.saveInProgress = false;
        this.propagateValue();
      }),
      map(() => true),
      catchError(() => {
        // background/timer flush: surfaced through the banner instead of a toast (silent mode);
        // the edits stay in memory and the next flush retries them
        this.saveInProgress = false;
        this.autosaveFailed = true;
        return of(false);
      }),
    );
  }

  // Uploads new ID documents and removes stored ones selected with the cross icon. New files keep
  // the legacy tags so they appear under the Documents tab and final-submission metadata fixup.
  private uploadPendingIdDocuments(rows: RowInfo[]) {
    const withDocs = rows.filter(row => (row.formControl.value?.idDocument ?? []).length > 0);
    const deletedFileNames = rows.flatMap(row => row.deletedIdDocuments.map(document => document.name));
    if (!withDocs.length && !deletedFileNames.length) {
      return of(true);
    }
    const uploads = collectIndividualIdDocumentUploads(withDocs.map(row => row.formControl.value!));
    const files = tagIndividualIdDocumentFiles(uploads, withDocs.map(row => row.individual), this.idDocumentMetadata);
    return from(filesToBase64Files(files)).pipe(
      switchMap(base64Files =>
        this.applicationService.uploadApplicationDocumentToAzureStorage(
          this.applicationId,
          base64Files,
          deletedFileNames,
        )),
      switchMap(() => this.applicationService.listApplicationDocumentFn(this.applicationId).pipe(
        catchError(() => of(null as AzureStorageDocument[] | null)),
      )),
      tap(documents => {
        for (const row of rows) {
          row.deletedIdDocuments = [];
          if (documents) {
            row.idDocuments = individualIdDocumentRefs(
              row.individual.idDocuments,
              documents,
              [row.individual.guid],
              `${row.individual.givenName ?? ''} ${row.individual.surname ?? ''}`,
            );
            row.idDocumentProvided = row.idDocuments.length > 0;
          } else if (withDocs.includes(row)) {
            row.idDocumentProvided = true;
          }
          if (!withDocs.includes(row)) continue;
          const v = row.formControl.value;
          if (v) {
            row.formControl.setValue({ ...v, idDocument: null });
          }
        }
      }),
      map(() => true),
    );
  }

  // ============ control value accessor / validator ============

  private propagateValue() {
    this.propagateChange({
      individuals: this.rows.map(row => row.individual),
      primaryContactId: this.formControlPrimaryContact.value,
    });
  }

  // The page owns the data through this component (loaded from the backend), so incoming writes
  // are ignored - the bound form control exists for validity gating and to expose the latest
  // saved individuals to the page.
  doWriteValue(_?: IndividualDetailsListValue | null): void {
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChangeFn = fn;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (!this.loaded) {
      return { individualDetailsLoading: true };
    }
    const errors: ValidationErrors = {};
    if (this.documentsLoadFailed) {
      errors['individualDocumentsUnavailable'] = true;
    }
    if (this.rows.some(row => row.formControl.invalid)) {
      errors['individualDetailsIncomplete'] = true;
    }
    if (this.rows.length && !this.formControlPrimaryContact.value) {
      errors['primaryContactRequired'] = true;
    }
    if (this.missingPropertyOwner()) {
      errors['propertyOwnerRequired'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  }

  // Read off the live form values rather than the loaded individuals so the requirement clears as
  // soon as the user ticks property owner, without waiting for the row to be flushed to the backend.
  missingPropertyOwner(): boolean {
    if (!this.propertyOwnerRequired || !this.loaded || !this.rows.length) {
      return false;
    }
    return !this.rows.some(row => this.extractPropertyOwner(row.formControl.value?.propertyOwner).isOwner);
  }

  mark(): void {
    this.formControlPrimaryContact.markAsTouched();
    for (const row of this.rows) {
      row.formControl.markAsTouched();
    }
    this.markObservable.next(true);
  }
}
